import { useState } from "react";
import {
  Link,
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import Dropdown from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import pluralize from "utils/pluralize";

import {
  getRemainingPaymentAttempts,
  handleUpdatePayment,
} from "./membershipHelper";

const load = async (params: {
  membershipId: string;
  membership: Models<"PlatformUserMembershipModel">;
}) => {
  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"));
  const platform = queryClient.fetchQuery(["platform"], () => get("/platform"));
  const membershipCancellationReasons = queryClient.fetchQuery(
    ["/platform-user-memberships/cancellation-reasons"],
    () => get("/platform-user-memberships/cancellation-reasons")
  );

  return waitObj({
    userInfo,
    membershipId: params.membershipId,
    platform,
    membership: params.membership,
    membershipCancellationReasons,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipDunningCancelLoader: LoaderFunction = async ({
  params: { membershipId },
}) => {
  if (!membershipId) return redirect("..");

  const userMemberships = await queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const membership = userMemberships.find(
    (m) => m.membership.id == membershipId
  );

  if (!membership || membership.dunningStage === 0) return redirect("..");

  return load({ membershipId, membership });
};

export const WalletMembershipDunningCancelPage = () => {
  const data = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const membership = data.membership;

  const cancellationReasons = data.membershipCancellationReasons.map((r) => ({
    ...r,
    name: r.reason,
  }));
  const [selectedCancellationReasonId, setSelectedCancellationReasonId] =
    useState<string>();
  const selectedCancellationReason = cancellationReasons.find(
    (r) => r.id === selectedCancellationReasonId
  );

  const [cancellationReasonError, setCancellationReasonError] =
    useState<boolean>(false);

  const numberOfAttemptsLeft =
    membership && getRemainingPaymentAttempts(membership.dunningStage);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          CANCEL MY MEMBERSHIP
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-8 pt-3 text-left">
        <div className="text-title">
          Are you sure you want to cancel your {membership?.membership.name}{" "}
          membership?
        </div>
        <div className="flex flex-col gap-y-5 text-body1">
          <div>
            Your membership and all benefits are currently paused due to failed
            payment(s). We will try again{" "}
            <span className="text-bold1">
              {numberOfAttemptsLeft} more{" "}
              {pluralize(numberOfAttemptsLeft ?? 0, "time", "times")}
            </span>{" "}
            before automatically canceling your membership. Click below to
            cancel now, or update your payment method to reactivate your
            membership.
          </div>
          <div>
            On cancellation, all of your membership benefits will end. Depending
            on your membership, any previously earned but unclaimed membership
            treatments may still be available to claim.
          </div>
          <div>
            When your request has been processed confirmation emails will be
            sent to <span className="text-bold1">{data.userInfo.email}</span>
          </div>
        </div>
        <div className="flex shrink">
          <Link
            to="/account/details"
            className="flex text-bold1 text-brand-color"
          >
            Change your email
          </Link>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 py-5">
        <Dropdown
          options={cancellationReasons}
          value={selectedCancellationReasonId}
          setValue={(value) => {
            setSelectedCancellationReasonId(value);
            setCancellationReasonError(false);
          }}
          textValue={selectedCancellationReason?.name}
          label="Cancellation reason"
          invalid={cancellationReasonError}
        />
        <Button
          fullWidth
          style="white"
          disabled={!selectedCancellationReasonId}
          onClick={() => {
            if (!selectedCancellationReasonId) {
              setCancellationReasonError(true);
              return;
            }

            navigate(
              `/wallet/memberships/manage/${membership?.membership.id}/dunning/cancel`,
              {
                state: {
                  membershipCancellationReasonId: selectedCancellationReasonId,
                },
              }
            );
          }}
        >
          Yes, cancel my membership
        </Button>
        <Button
          fullWidth
          style="primary"
          onClick={() => {
            if (!membership) {
              return;
            }
            handleUpdatePayment(membership?.id, data.platform, () => {
              navigate("/wallet/memberships");
            });
          }}
        >
          Update payment method
        </Button>
      </div>
    </div>
  );
};
